'use client';
import { type FileRejection } from '@curology/ui-components-web';
import { Button } from '@curology/ui-components-web/react/Button';
import { ImageUpload } from '@curology/ui-components-web/react/FileUpload';
import TrashIcon from '@curology/ui-components-web/react/Icon/icons/TrashIcon';
import { Typography } from '@curology/ui-components-web/react/Typography';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { type Brand } from '@/consts/brands';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import { type PhotoResource } from '@/services/funnelService/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import { acceptMediaAndroid } from '@/utils/images/acceptMediaAndroid';

import { type QuestionPhotoUploadModel } from '../../../config/questions/photoUploadQuestion';
import { type PhotoUploadFile } from '../types/types';
import {
  handleFileAdded,
  handleFileDeleted,
  handleFileRejected,
} from '../utils/handleFiles';
import {
  getPhotoUploaderIcon,
  cleanInvalidFileTypeMessage,
} from '../utils/helpers';

const PhotoUploader = ({
  brand,
  photo,
  question,
  requiredPhotoTypes,
  setSubmitButtonDisabled,
  setUploadedFiles,
  treatmentCategory,
  uploadedFiles,
}: {
  brand: Brand;
  photo: PhotoResource;
  question: QuestionPhotoUploadModel;
  requiredPhotoTypes: string[];
  setSubmitButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadedFiles: React.Dispatch<React.SetStateAction<PhotoUploadFile[]>>;
  treatmentCategory?: TreatmentCategory;
  uploadedFiles: PhotoUploadFile[];
}) => {
  const [error, setError] = React.useState<Error | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const flash = useFlashMessages();
  const { handleIfSessionExpired } = useSessionExpired(brand);

  const photoUploadIcon = getPhotoUploaderIcon(photo);

  const { imagesHeight, imagesWidth } = photo;
  const aspectRatio = `${imagesWidth / imagesHeight}`;

  const photoTypeFiles = uploadedFiles.filter(
    (file: PhotoUploadFile) => file.photoType === photo.type,
  );

  const newPhotoContainerClasses = clsx(
    'cur-relative',
    photoTypeFiles.length !== 0 ? 'cur-mt-4' : '',
  );

  useEffect(() => {
    if (error) {
      flash.error(cleanInvalidFileTypeMessage(error));
    }
  }, [error]);

  const onFileAdded = async (files: File[]) => {
    let earlyError: Error | undefined;
    try {
      await handleFileAdded(
        brand,
        files,
        photo,
        question,
        requiredPhotoTypes,
        setSubmitButtonDisabled,
        setUploadedFiles,
        setUploading,
        treatmentCategory,
        (err) => {
          earlyError = err;
          setError(err);
          trackEvent(AnalyticsEvent.PHOTO_UPLOAD_FAILED, {
            error: err.message,
          });
        },
      );
      if (!earlyError) {
        setError(undefined);
      }
    } catch (e) {
      if (handleIfSessionExpired((e as any).status)) {
        setUploading(false);
        return;
      }
      const message = e instanceof Error ? e.message : 'Unknown error';
      setError(new Error(message));
      setUploading(false);
      trackEvent(AnalyticsEvent.PHOTO_UPLOAD_FAILED, {
        error: message,
      });
    }
  };

  /**
   * Override the file input's `accept` parameter for Android so we offer the user
   * the choice of file picker or camera to the user. Should use the Dropzone default for
   * all platforms expect Android, which will use the non-standard: `accept="image/*;capture=camera"`
   * See this gist for more details: https://gist.github.com/danawoodman/4788404bc620d5392d111dba98c73873
   */
  const acceptAttribute = acceptMediaAndroid(navigator);

  return (
    <div className="cur-flex-1 cur-text-center">
      <div className="cur-pl-2">
        <Typography className="cur-mb-2" weight="bold">
          Your photo
        </Typography>
        <div className="cur-flex cur-flex-col">
          {photoTypeFiles.map((photoTypeFile, index) => {
            const existingPhotoContainerClasses = clsx(
              'cur-relative',
              index !== 0 ? 'cur-mt-4' : '',
            );

            return (
              <div className={existingPhotoContainerClasses} key={index}>
                <ImageUpload
                  className="cur-box-border cur-h-full"
                  complete={photoTypeFile.file}
                  {...acceptAttribute}
                  completeAction={
                    <Button
                      buttonType="text"
                      icon={<TrashIcon />}
                      onClick={() => {
                        handleFileDeleted(
                          brand,
                          photoTypeFile.photoId,
                          requiredPhotoTypes,
                          setSubmitButtonDisabled,
                          setUploadedFiles,
                        );
                      }}
                      size="sm"
                      title="Delete?"
                    />
                  }
                  data-testid={`photo-uploader-uploaded-image-${index}`}
                  error={error}
                  imageOverflowHidden={true}
                  initialLabel={photo.uploadImageText}
                  onFileAdded={onFileAdded}
                  onFileRejected={(fileRejections: FileRejection[]) =>
                    handleFileRejected(fileRejections, setError)
                  }
                  src={photoTypeFile.file}
                  style={{
                    aspectRatio,
                  }}
                />
              </div>
            );
          })}
          {(photoTypeFiles.length === 0 || photo.allowsMultiple) && (
            <div className={newPhotoContainerClasses}>
              <ImageUpload
                className="cur-box-border cur-h-full"
                complete={false}
                {...acceptAttribute}
                data-testid="photo-uploader-new-image-to-upload"
                error={error}
                icon={photoUploadIcon}
                iconSize="md"
                initialLabel={photo.uploadImageText}
                onFileAdded={onFileAdded}
                onFileRejected={(fileRejections: FileRejection[]) =>
                  handleFileRejected(fileRejections, setError)
                }
                style={{
                  aspectRatio,
                }}
                uploading={uploading}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoUploader;
